/**
 * CustomTemplatesManager — CRUD pentru template-uri personale de lucrări.
 * Utilizatorul își poate defini propriile "rețete" cu articole și prețuri.
 */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Save, Edit, X, BookOpen, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyTemplate = () => ({
  name: "",
  category: "Personal",
  description: "",
  items: [
    { code: "A01", name: "", um: "mp", materialPrice: 0, manoperaPrice: 0, qty_factor: 1.0, use: "area" },
  ],
});

export default function CustomTemplatesManager({ companyId, open, onOpenChange, onChanged }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // template being edited or null
  const [saving, setSaving] = useState(false);

  const params = useMemo(() => ({ company_id: companyId }), [companyId]);

  const fetchList = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/sketches/custom-templates`, { params });
      setList(data || []);
    } catch (e) {
      toast.error("Nu am putut încărca șabloanele");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) fetchList(); /* eslint-disable-next-line */ }, [open]);

  const handleSave = async () => {
    if (!editing.name.trim()) { toast.error("Denumire obligatorie"); return; }
    if (!editing.items?.length) { toast.error("Adaugă cel puțin un articol"); return; }
    if (editing.items.some((it) => !it.name?.trim())) { toast.error("Toate articolele trebuie să aibă denumire"); return; }
    try {
      setSaving(true);
      if (editing.id) {
        await axios.patch(`${API}/sketches/custom-templates/${editing.id}`, editing, { params });
      } else {
        await axios.post(`${API}/sketches/custom-templates`, editing, { params });
      }
      toast.success("Șablon salvat");
      setEditing(null);
      await fetchList();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Șterge șablonul „${t.name}"?`)) return;
    try {
      await axios.delete(`${API}/sketches/custom-templates/${t.id}`, { params });
      toast.success("Șablon șters");
      fetchList();
      onChanged && onChanged();
    } catch (e) {
      toast.error("Eroare la ștergere");
    }
  };

  const updateItem = (idx, patch) => {
    setEditing((e) => ({ ...e, items: e.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  };
  const addItem = () => {
    setEditing((e) => ({
      ...e,
      items: [
        ...e.items,
        { code: `A${String(e.items.length + 1).padStart(2, "0")}`, name: "", um: "mp", materialPrice: 0, manoperaPrice: 0, qty_factor: 1.0, use: "area" },
      ],
    }));
  };
  const removeItem = (idx) => {
    setEditing((e) => ({ ...e, items: e.items.filter((_, i) => i !== idx) }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="custom-templates-manager">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-violet-600" />
            Șabloane de lucrări personale
          </DialogTitle>
          <DialogDescription>
            Definește-ți propriile "rețete" cu articole și prețuri pentru generare rapidă de devize.
          </DialogDescription>
        </DialogHeader>

        {editing ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Denumire *</label>
                <Input
                  value={editing.name}
                  onChange={(e) => setEditing((v) => ({ ...v, name: e.target.value }))}
                  placeholder="Ex: Hidroizolație bituminoasă premium"
                  data-testid="custom-tpl-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Categorie</label>
                <Input
                  value={editing.category || ""}
                  onChange={(e) => setEditing((v) => ({ ...v, category: e.target.value }))}
                  placeholder="Personal"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Descriere</label>
              <Textarea
                value={editing.description || ""}
                onChange={(e) => setEditing((v) => ({ ...v, description: e.target.value }))}
                rows={2}
                placeholder="Scurtă descriere a sistemului"
              />
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                <div className="text-sm font-semibold text-slate-700">Articole ({editing.items.length})</div>
                <Button size="sm" onClick={addItem} className="h-7 bg-teal-600 hover:bg-teal-700 text-white" data-testid="custom-tpl-add-item">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Adaugă articol
                </Button>
              </div>
              <div className="max-h-[360px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 text-slate-600 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5 w-16">Cod</th>
                      <th className="text-left px-2 py-1.5">Denumire *</th>
                      <th className="text-left px-2 py-1.5 w-16">UM</th>
                      <th className="text-right px-2 py-1.5 w-20">Mat. RON</th>
                      <th className="text-right px-2 py-1.5 w-20">Manop. RON</th>
                      <th className="text-right px-2 py-1.5 w-20" title="Factor pierderi ex: 1.10 = +10%">Factor</th>
                      <th className="text-left px-2 py-1.5 w-24">Aplică la</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {editing.items.map((it, idx) => (
                      <tr key={idx} className="border-t border-slate-100">
                        <td className="px-1 py-1"><Input className="h-7 text-xs" value={it.code} onChange={(e) => updateItem(idx, { code: e.target.value })} /></td>
                        <td className="px-1 py-1"><Input className="h-7 text-xs" value={it.name} onChange={(e) => updateItem(idx, { name: e.target.value })} placeholder="Denumire articol" /></td>
                        <td className="px-1 py-1"><Input className="h-7 text-xs" value={it.um} onChange={(e) => updateItem(idx, { um: e.target.value })} /></td>
                        <td className="px-1 py-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" min="0" value={it.materialPrice} onChange={(e) => updateItem(idx, { materialPrice: parseFloat(e.target.value) || 0 })} /></td>
                        <td className="px-1 py-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" min="0" value={it.manoperaPrice} onChange={(e) => updateItem(idx, { manoperaPrice: parseFloat(e.target.value) || 0 })} /></td>
                        <td className="px-1 py-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" min="0.01" value={it.qty_factor} onChange={(e) => updateItem(idx, { qty_factor: parseFloat(e.target.value) || 1 })} /></td>
                        <td className="px-1 py-1">
                          <Select value={it.use} onValueChange={(v) => updateItem(idx, { use: v })}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="area">Arie (m²)</SelectItem>
                              <SelectItem value="perimeter">Perimetru (ml)</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-1 py-1 text-center">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => removeItem(idx)} disabled={editing.items.length <= 1}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-1.5 bg-amber-50 text-amber-800 text-[11px] border-t border-amber-200">
                * <b>Factor</b> = multiplicator pentru pierderi/suprapuneri (1.10 = +10%). <b>Aplică la</b>: Arie folosește total_arie × factor; Perimetru folosește total_perim × factor.
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditing(null)}><X className="w-4 h-4 mr-1" /> Anulează</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="custom-tpl-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Salvează
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="text-sm text-slate-600">{list.length} șablon(e) personal(e)</div>
              <Button size="sm" onClick={() => setEditing(emptyTemplate())} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="custom-tpl-new-btn">
                <Plus className="w-4 h-4 mr-1" /> Șablon nou
              </Button>
            </div>
            {loading ? (
              <div className="py-8 text-center text-slate-500">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Se încarcă…
              </div>
            ) : list.length === 0 ? (
              <div className="text-center py-10 text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
                <BookOpen className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                Nu ai încă șabloane personale. Creează primul cu prețurile tale!
              </div>
            ) : (
              <div className="space-y-2">
                {list.map((t) => (
                  <div key={t.id} className="border border-slate-200 rounded-lg p-3 bg-white hover:border-teal-300 transition">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm text-slate-800">{t.name}</div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <Badge variant="outline" className="text-[10px]">{t.category}</Badge>
                          <Badge variant="outline" className="text-[10px]">{t.items?.length || 0} articole</Badge>
                        </div>
                        {t.description && <p className="text-xs text-slate-600 mt-1.5">{t.description}</p>}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setEditing(t)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-red-500 hover:bg-red-50" onClick={() => handleDelete(t)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
